// src/component/customerHistoryPatternPage/CustomerHistoryPatternsPage.js

import { useImmer } from "use-immer";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import LocationService from "../service/RouteUtilService";
import ApiConnector from "../connector/ApiConnector";

import CustomerHistoryPatternsFilter from "../component/customerHistoryPatternPage/CustomerHistoryPatternsFilter";
import CustomerHistoryPatternsColumnConfigurator from "../component/customerHistoryPatternPage/CustomerHistoryPatternsColumnConfigurator";
import CustomerHistoryPatternsTable from "../component/customerHistoryPatternPage/CustomerHistoryPatternsTable";

import {
  faAnglesLeft,
  faAnglesRight,
  faAngleLeft,
  faAngleRight
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReportPageService from "../service/ReportPageService";

const FILTER_TEMPLATE = {
  companyId: "",
  merchantCode: "",
  tier: "",
  dominantHour: "",
  slotsValidMin: null,
  slotsValidMax: null,
  pageSize: 30,
  pageNumber: 0
};

const CustomerHistoryPatternsPage = () => {
  const [filterConfig, updateFilterConfig] = useImmer(FILTER_TEMPLATE);
  const [data, updateData] = useImmer([]);
  const [displayAttributes, updateDisplayAttributes] = useImmer([]);

  const isLoadInProgress = useRef(false);
  const location = useLocation();

  const [totalCount, setTotalCount] = useImmer(0);

  const startIndex = filterConfig.pageNumber * filterConfig.pageSize;
  const endIndex = startIndex + data.length;

  const canGoPrev = filterConfig.pageNumber > 0;
  const canGoNext = endIndex < totalCount;

  const [generalConfig, updateGeneralConfig] = useImmer(null);
  const lastUpdated = data.length > 0 ? data[0].lastUpdated : null;

  const HIDDEN_COLUMNS = ["lastUpdated"];

  // Load initial URL params ONCE
  useEffect(() => {
    updateFilterConfig((draft) => {
      draft.companyId = LocationService.getParam(location, "companyId", "");
      draft.merchantCode = LocationService.getParam(location, "merchantCode", "");
      draft.tier = LocationService.getParam(location, "tier", "");
      draft.dominantHour = LocationService.getParam(location, "dominantHour", "");

      const slotsValidMin = LocationService.getParam(location, "slotsValidMin", null);
      const slotsValidMax = LocationService.getParam(location, "slotsValidMax", null);

      draft.slotsValidMin = slotsValidMin ? Number(slotsValidMin) : null;
      draft.slotsValidMax = slotsValidMax ? Number(slotsValidMax) : null;

      draft.pageSize = Number(LocationService.getParam(location, "pageSize", 30));
      draft.pageNumber = Number(LocationService.getParam(location, "pageNumber", 0));
    });
  }, []);

  useEffect(() => {
    (async () => {
      const generalConfig = await ApiConnector.getLatestGeneralConfig();
      updateGeneralConfig(generalConfig.generalConfig.customerHistoryPatternUpdateConfig);
    })();
  }, []);

  // Build query
  const buildQueryParams = () => ({
    companyId: filterConfig.companyId || null,
    merchantCode: filterConfig.merchantCode || null,
    tier: filterConfig.tier || null,
    dominantHour: filterConfig.dominantHour || null,
    slotsValidMin: filterConfig.slotsValidMin ?? null,
    slotsValidMax: filterConfig.slotsValidMax ?? null,
    pageSize: filterConfig.pageSize,
    offset: filterConfig.pageNumber * filterConfig.pageSize,
    displayCount: true
  });


  //
  // Load data
  //
  const loadData = async () => {
    if (isLoadInProgress.current) return;
    isLoadInProgress.current = true;

    try {
      const response = await ApiConnector.getCustomerHistoryPatterns(buildQueryParams());
      updateData(response.data || []);
      setTotalCount(response.count || 0);
    } finally {
      isLoadInProgress.current = false;
    }
  };


  //
  // Load on mount + whenever filters change
  //
  useEffect(() => {
    loadData();
  }, [
    filterConfig.companyId,
    filterConfig.merchantCode,
    filterConfig.tier,
    filterConfig.dominantHour,
    filterConfig.slotsValidMin,
    filterConfig.slotsValidMax,
    filterConfig.pageSize,
    filterConfig.pageNumber
  ]);

  // Column auto-discovery
  useEffect(() => {
    if (data.length === 0) return;
    if (displayAttributes.length !== 0) return;

    const first = data[0];
    updateDisplayAttributes(
      Object.keys(first).filter(k => !HIDDEN_COLUMNS.includes(k))
    );
  }, [data]);

  // Paging
  const setPageNumber = (uiValue) => {
    updateFilterConfig(draft => {
      const totalPages = Math.ceil(totalCount / draft.pageSize);

      let internal = Number(uiValue) - 1; // UI = 1-based → internal = 0-based

      internal = Math.max(0, Math.min(totalPages - 1, internal));

      draft.pageNumber = internal;
    });
  };



  const totalPages = Math.ceil(totalCount / filterConfig.pageSize);

  // Wait until generalConfig is loaded, otherwise UI will crash
  if (!generalConfig) return null;

  return (
    <>
      <header className="subheader header-with-save">
        <h1>Customer History Patterns</h1>
      </header>

      <main>

        {/* Paging header */}
        <div className="row">
          <div className="col"><h2>Patterns</h2></div>

          <div className="col">
            <h2 className="float-right">
              <FontAwesomeIcon icon={faAnglesLeft} onClick={() => canGoPrev && setPageNumber((filterConfig.pageNumber + 1) - 10)} className={canGoPrev ? "icon-clickable" : "icon-disabled"} />
              &nbsp;&nbsp;
              <FontAwesomeIcon icon={faAngleLeft} onClick={() => canGoPrev && setPageNumber((filterConfig.pageNumber + 1) - 1)} className={canGoPrev ? "icon-clickable" : "icon-disabled"} />
              &nbsp;&nbsp;
              <FontAwesomeIcon icon={faAngleRight} onClick={() => canGoNext && setPageNumber((filterConfig.pageNumber + 1) + 1)} className={canGoNext ? "icon-clickable" : "icon-disabled"} />
              &nbsp;&nbsp;
              <FontAwesomeIcon icon={faAnglesRight} onClick={() => canGoNext && setPageNumber((filterConfig.pageNumber + 1) + 10)} className={canGoNext ? "icon-clickable" : "icon-disabled"} />
              &nbsp;&nbsp;&nbsp;
            </h2>
          </div>
        </div>


        <details>
          <summary>Filtering parameters</summary>

          <CustomerHistoryPatternsFilter
            filterConfig={filterConfig}
            onChange={(newConfig) =>
              updateFilterConfig(() => ({
                ...newConfig,
                pageNumber: 0
              }))
            }
          />

          <details>
            <summary>Displayed columns</summary>
            <CustomerHistoryPatternsColumnConfigurator
              sampleData={data}
              onChange={updateDisplayAttributes}
            />
          </details>
        </details>

        <CustomerHistoryPatternsTable
          patterns={data}
          displayedAttributes={displayAttributes}
          totalSlots={generalConfig.totalSlots}
        />

        <div style={{
          display: "flex",
          justifyContent: "space-between"
        }}>
          <p>Page number: {filterConfig.pageNumber + 1} / {totalPages}</p>
          {lastUpdated && (<p>Last updated: {ReportPageService.renderPrettyValue(lastUpdated)}</p>)}
        </div>

      </main>
    </>
  );
};

export default CustomerHistoryPatternsPage;
